import { Download } from 'lucide-react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { nativeImageGenerate } from 'sprites-types/workflow/tools/nativeImageGenerate'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { FileResult } from '../shared/FileResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function NativeLlmImageGenerationDisplayComponent({
  nodeExecution,
  workflowConfig: _workflowConfig,
  onOpenPanel,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeImageGenerate, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Handle both single result and array of results
  const results = Array.isArray(result) ? result : [result]
  const isMultiple = Array.isArray(result)

  const handleDownloadAll = async () => {
    if (!isMultiple) return

    for (let i = 0; i < results.length; i++) {
      if (results[i].source?.value) {
        try {
          const response = await fetch(results[i].source.value)
          if (!response.ok) throw new Error('Network response was not ok')

          const blob = await response.blob()
          const blobUrl = window.URL.createObjectURL(blob)

          const downloadLink = document.createElement('a')
          downloadLink.href = blobUrl
          downloadLink.download = results[i].filename || `generated-image-${i + 1}.png`
          downloadLink.click()

          window.URL.revokeObjectURL(blobUrl)

          // Add a small delay between downloads to avoid overwhelming the browser
          if (i < results.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500))
          }
        } catch (error) {
          console.error('Download failed:', error)
          toast.error(`Download failed for image ${i + 1}`)
        }
      }
    }
  }

  const handlePreview = (imageUrl: string) => {
    if (imageUrl) {
      window.open(imageUrl, '_blank')
    }
  }

  // Check if we have any valid images
  const hasValidImages = results.some((r) => r.source?.value)

  if (!hasValidImages) {
    return (
      <div className="rounded-lg border bg-gray-50 p-4">
        <p className="text-sm text-gray-600">No image generated</p>
      </div>
    )
  }

  // For single image, just render one FileResult
  if (!isMultiple) {
    const singleResult = results[0]
    if (!singleResult?.source?.value) return null

    return (
      <FileResult
        file={{
          name: singleResult.filename || 'Generated Image',
          url: singleResult.source.value,
          downloadUrl: singleResult.source.value,
          mimeType: 'image/png',
          id: 'generated-image',
        }}
        customIcon={
          <div className="h-5 w-5 rounded bg-gradient-to-br from-purple-500 to-pink-500"></div>
        }
        customLabel="Generated Image"
        customSubtitle="AI-generated image ready"
        onOpenPanel={onOpenPanel}
        showPreview={true}
        onImageClick={handlePreview}
      />
    )
  }

  // For multiple images, create a header with bulk actions and individual FileResult components
  const bulkContextActions = [
    {
      id: 'download-all',
      label: 'Download All',
      icon: <Download className="h-4 w-4" />,
      onClick: handleDownloadAll,
    },
  ]

  return (
    <div className="space-y-3">
      {/* Header for multiple images with bulk actions */}
      <ContextMenuWrapper actions={bulkContextActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <div className="group flex items-center gap-4 bg-white px-4 py-3">
            <div className="flex-shrink-0">
              <div className="h-5 w-5 rounded bg-gradient-to-br from-purple-500 to-pink-500"></div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Generated Images</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {results.length} AI-generated images ready
              </div>
            </div>
          </div>
        </div>
      </ContextMenuWrapper>

      {/* Individual image FileResults */}
      <div className="space-y-2">
        {results.map(
          (singleResult, index) =>
            singleResult.source?.value && (
              <FileResult
                key={index}
                file={{
                  name: singleResult.filename || `Generated Image ${index + 1}`,
                  url: singleResult.source.value,
                  downloadUrl: singleResult.source.value,
                  mimeType: 'image/png',
                  id: `generated-image-${index}`,
                }}
                customIcon={
                  <div className="h-5 w-5 rounded bg-gradient-to-br from-purple-500 to-pink-500"></div>
                }
                customLabel={`Generated Image ${index + 1}`}
                customSubtitle="AI-generated image"
                onOpenPanel={onOpenPanel}
                showPreview={true}
                onImageClick={handlePreview}
                className="mx-8"
              />
            )
        )}
      </div>
    </div>
  )
}

async function downloadImage(nodeExecution: NodeExecution) {
  const schema = getToolResultSchema(nativeImageGenerate, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    console.error('Schema validation failed for download:', parseResult.error)
    return Promise.resolve()
  }

  const result = parseResult.data

  // Handle both single result and array of results
  const results = Array.isArray(result) ? result : [result]
  const isMultiple = Array.isArray(result)

  try {
    for (let i = 0; i < results.length; i++) {
      const singleResult = results[i]
      if (!singleResult.source?.value) continue

      const response = await fetch(singleResult.source.value)
      if (!response.ok) throw new Error('Network response was not ok')

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download =
        singleResult.filename ||
        (isMultiple ? `generated-image-${i + 1}.png` : 'generated-image.png')
      downloadLink.click()

      window.URL.revokeObjectURL(blobUrl)

      // Add a small delay between downloads for multiple images
      if (isMultiple && i < results.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500))
      }
    }
  } catch (error) {
    console.error('Download failed:', error)
    toast.error('Download failed')
  }
}

// Convert image generation results to markdown
function nativeImageGenerateToMarkdown(nodeExecution: NodeExecution): string {
  try {
    const schema = getToolResultSchema(nativeImageGenerate, nodeExecution.inputs)
    const parseResult = schema.safeParse(nodeExecution.outputs)

    if (!parseResult.success) {
      return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
    }

    const result = parseResult.data

    // Handle both single result and array of results
    const results = Array.isArray(result) ? result : [result]

    if (results.length === 0) {
      return `## AI Image Generation\n\nNo images generated.\n\n---\n`
    }

    if (results.length === 1) {
      return `![Generated Image](${results[0].source?.value})\n`
    }

    // Multiple images
    let markdown = `## AI Image Generation (${results.length} images)\n\n`
    results.forEach((singleResult, index) => {
      if (singleResult.source?.value) {
        markdown += `![Generated Image ${index + 1}](${singleResult.source.value})\n\n`
      }
    })
    markdown += `---\n`

    return markdown
  } catch (error) {
    return `## AI Image Generation\n\nError parsing results: ${error}\n\n---\n`
  }
}

// Export the component with markdown support
export const NativeLlmImageGenerationDisplay = createWorkflowToolComponent(
  NativeLlmImageGenerationDisplayComponent,
  downloadImage,
  nativeImageGenerateToMarkdown
)
